/**
 * The Market API — same guest-token pattern as the Curve: a device token in
 * localStorage rides the x-ft-market-guest header until login, then
 * claimGuestPortfolio() attaches the portfolio to the user.
 */

export const CHIPS_LABEL = 'chips';

const GUEST_KEY = 'ftMarketGuest';

export function getGuestToken() {
  let token = null;
  try {
    token = localStorage.getItem(GUEST_KEY);
    if (!token) {
      token =
        window.crypto && window.crypto.randomUUID
          ? window.crypto.randomUUID()
          : `g-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(GUEST_KEY, token);
    }
  } catch (e) {
    // storage unavailable — server will treat every call as a fresh guest
  }
  return token;
}

export function clearGuestToken() {
  try {
    localStorage.removeItem(GUEST_KEY);
  } catch (e) {
    // ignore
  }
}

async function request(path, options = {}) {
  const guest = getGuestToken();
  const res = await fetch(`/api/market${path}`, {
    credentials: 'include',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(guest ? { 'x-ft-market-guest': guest } : {}),
      ...options.headers,
    },
  });
  let body = null;
  try {
    body = await res.json();
  } catch (e) {
    body = {};
  }
  if (!res.ok) {
    const err = new Error((body && body.error) || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return body;
}

export const fetchPortfolio = () => request('/me');
export const placeOrder = (order) =>
  request('/orders', { method: 'POST', body: JSON.stringify(order) });
export const cancelOrder = (orderId) => request(`/orders/${orderId}`, { method: 'DELETE' });
export const closePosition = (positionId, amount) =>
  request(`/positions/${positionId}/close`, {
    method: 'POST',
    body: JSON.stringify(amount ? { amount } : {}),
  });
export const setLeaderboardOptIn = (optIn) =>
  request('/leaderboard/opt-in', { method: 'POST', body: JSON.stringify({ optIn }) });

export const searchAssets = ({ query, position, limit } = {}) => {
  const params = new URLSearchParams();
  if (query) params.set('query', query);
  if (position) params.set('position', position);
  if (limit) params.set('limit', limit);
  const qs = params.toString();
  return request(`/assets${qs ? `?${qs}` : ''}`);
};
export const fetchMovers = () => request('/assets/movers');
export const fetchAsset = (playerId) => request(`/assets/${playerId}`);
export const watchAsset = (playerId) => request(`/watchlist/${playerId}`, { method: 'POST' });
export const unwatchAsset = (playerId) => request(`/watchlist/${playerId}`, { method: 'DELETE' });

export const fetchPickAssets = () => request('/assets/picks');
export const fetchPickAsset = (label) => request(`/assets/pick/${encodeURIComponent(label)}`);
export const watchPick = (label) =>
  request(`/watchlist/pick/${encodeURIComponent(label)}`, { method: 'POST' });
export const unwatchPick = (label) =>
  request(`/watchlist/pick/${encodeURIComponent(label)}`, { method: 'DELETE' });

export const fetchLeaderboard = () => request('/leaderboard');

export async function claimGuestPortfolio() {
  const guest = getGuestToken();
  if (!guest) return { claimed: false };
  const result = await request('/claim', {
    method: 'POST',
    body: JSON.stringify({ guestToken: guest }),
  });
  if (result.claimed) clearGuestToken();
  return result;
}

// ── chart ranges ──
// KTC history for most assets is thin (players often a few months, picks
// often just weeks), so ranges stay short and honest rather than implying
// depth we don't have. `days: 'ytd'` cuts to Jan 1 of the current year.
export const CHART_RANGES = [
  { key: '1W', days: 7 },
  { key: '1M', days: 30 },
  { key: 'YTD', days: 'ytd' },
  { key: 'ALL', days: null },
];

export function rangeCutoff(key) {
  const r = CHART_RANGES.find((x) => x.key === key);
  if (!r || r.days === null) return null;
  if (r.days === 'ytd') return `${new Date().getFullYear()}-01-01`;
  return new Date(Date.now() - r.days * 86400000).toISOString().slice(0, 10);
}

// ── formatters ──

export const fmtChips = (n) =>
  n === null || n === undefined ? '—' : Math.round(n).toLocaleString('en-US');

export const fmtK = (v) => {
  if (v === null || v === undefined) return '—';
  return v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(v);
};

export const fmtShares = (s) => {
  if (s === null || s === undefined || Number.isNaN(s)) return '—';
  return s.toFixed(2).replace(/\.00$/, '');
};

// Singular only when the *displayed* value is exactly 1 — matches fmtShares'
// own rounding instead of re-deriving it (1.004 shows as "1", so it's singular too).
export const shareWord = (s) => {
  const shown = fmtShares(s);
  return shown === '1' || shown === '-1' ? 'share' : 'shares';
};

export const chipWord = (n) =>
  n !== null && n !== undefined && !Number.isNaN(n) && Math.abs(Math.round(n)) === 1 ? 'chip' : 'chips';

export const fmtPct = (p, digits = 1) => {
  if (p === null || p === undefined || Number.isNaN(p)) return '—';
  const sign = p > 0 ? '+' : '';
  return `${sign}${p.toFixed(digits)}%`;
};

// ── custom-amount input ──
// Strip everything but digits (so pasted/typed "25,000" still parses), then
// re-render with comma grouping so the field matches the rest of the UI.
export const parseChipsInput = (raw) => String(raw ?? '').replace(/[^0-9]/g, '');

export const formatChipsInput = (raw) => {
  const digits = parseChipsInput(raw);
  return digits ? Number(digits).toLocaleString('en-US') : '';
};
